import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from "../../config";
import {
  Calendar,
  Clock,
  Plus,
  Edit,
  Trash2,
  Users,
  AlertCircle,
  CheckCircle,
  Filter,
  Search,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
  Globe,
  EyeOff
} from 'lucide-react';
import './CalendarManagement.css';
import ActionIconButton from '../../Components/ActionIconButton';
import ActionsCell from '../../Components/ActionsCell';

const CalendarManagement = () => {
  const deriveAcademicYearFromDate = (date) => {
    const d = date instanceof Date ? date : new Date();
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const startYear = month >= 7 ? year : year - 1;
    const endYearShort = String(startYear + 1).slice(-2);
    return `${startYear}-${endYearShort}`;
  };

  const buildACPPEvents = (rows = []) => {
    return (rows || [])
      .filter((row) => row?.eventDate)
      .map((row, index) => ({
        _id: row._id || `acpp-${row.eventDate}-${index}`,
        title: row.eventTitle || `${row.subject || "Subject"} Plan`,
        color: "#1D4ED8",
        event_type: "acpp-plan",
        is_acpp_event: true,
        start_date: `${row.eventDate}T08:00:00`,
        source_meta: {
          grade: row.grade || "",
          section: row.section || "",
          subject: row.subject || "",
          term: row.term || "",
          chapter: row.chapter || "",
        },
      }));
  };

  const mergeCalendarDataWithACPP = (baseData = {}, acppEvents = []) => {
    const merged = { ...baseData };
    (acppEvents || []).forEach((event) => {
      const dateKey = String(event.start_date || "").split("T")[0];
      if (!dateKey) return;
      const dayData = merged[dateKey] || { events: [], tasks: [] };
      merged[dateKey] = {
        ...dayData,
        events: [event, ...(dayData.events || [])],
      };
    });
    return merged;
  };

  // State management
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('calendar'); // calendar, tasks, events
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('task'); // task, event
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data state
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [acppCalendarRows, setAcppCalendarRows] = useState([]);
  const [stats, setStats] = useState({
    urgent_tasks: 0,
    important_tasks: 0,
    completed_today: 0,
    assigned_tasks: 0,
    upcoming_events: 0,
    overdue_tasks: 0
  });

  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    assignedTo: '',
    search: '',
    dueDateFrom: '',
    dueDateTo: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    priority: 'normal',
    category: 'academic',
    assignedTo: '',
    eventType: 'academic',
    location: '',
    isAllDay: false,
    color: '#3B82F6'
  });

  // API base URL imported from config

  // Fetch data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch calendar data when date changes
  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  // API helper function
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Test backend connection
  const testBackend = async () => {
    try {
      const result = await apiCall('/calendar/test');
      console.log('Backend test successful:', result);
    } catch (err) {
      console.error('Backend test failed:', err);
    }
  };

  // Fetch initial data
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Test backend first
      await testBackend();

      const [statsData, staffData] = await Promise.all([
        apiCall('/calendar/stats'),
        apiCall('/calendar/staff')
      ]);

      setStats(statsData);
      setStaffMembers(staffData);
    } catch (err) {
      setError(`Failed to load initial data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch calendar data for current month
  const fetchCalendarData = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const acppAcademicYear = deriveAcademicYearFromDate(currentDate);

      const data = await apiCall(`/calendar/calendar-data?year=${year}&month=${month}`);
      let mergedCalendarData = data.calendar_data || {};

      try {
        const acppData = await apiCall(
          `/admin/acpp/module/academicCalendar?academic_year=${encodeURIComponent(acppAcademicYear)}`
        );
        const rows = acppData?.calendar_rows || [];
        setAcppCalendarRows(rows);
        const monthPrefix = `${year}-${String(month).padStart(2, "0")}-`;
        const acppMonthEvents = buildACPPEvents(rows).filter((event) =>
          String(event.start_date || "").startsWith(monthPrefix)
        );
        mergedCalendarData = mergeCalendarDataWithACPP(mergedCalendarData, acppMonthEvents);
      } catch (acppErr) {
        if (!String(acppErr?.message || "").includes("404")) {
          console.error("Failed to fetch ACPP Module 10 calendar data:", acppErr);
        }
        setAcppCalendarRows([]);
      }

      setCalendarData(mergedCalendarData);
    } catch (err) {
      console.error('Failed to fetch calendar data:', err);
    }
  };

  // Fetch tasks with filters (status/priority/category are server-side; search,
  // due date range and assignedTo are applied client-side below since assigned-to
  // values are only known once tasks are loaded)
  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();
      ['status', 'priority', 'category'].forEach((key) => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const data = await apiCall(`/calendar/tasks?${params.toString()}`);
      setTasks(data.tasks || []);
    } catch (err) {
      setError(`Failed to fetch tasks: ${err.message}`);
    }
  };

  // Client-side filters applied on top of the fetched tasks (search, due date range, assigned to)
  const distinctAssignees = Array.from(
    new Set(tasks.map(t => t.assigned_to_name).filter(Boolean))
  ).sort();

  const displayedTasks = tasks.filter(task => {
    const matchesSearch = !filters.search ||
      (task.title || '').toLowerCase().includes(filters.search.toLowerCase());

    const matchesAssignedTo = !filters.assignedTo || task.assigned_to_name === filters.assignedTo;

    const dueDate = task.due_date ? new Date(task.due_date) : null;
    const matchesDueFrom = !filters.dueDateFrom ||
      (dueDate && dueDate >= new Date(filters.dueDateFrom));
    const matchesDueTo = !filters.dueDateTo ||
      (dueDate && dueDate <= new Date(`${filters.dueDateTo}T23:59:59`));

    return matchesSearch && matchesAssignedTo && matchesDueFrom && matchesDueTo;
  });

  // Fetch events
  const fetchEvents = async () => {
    try {
      const data = await apiCall('/calendar/events');
      setEvents(data.events || []);
    } catch (err) {
      setError(`Failed to fetch events: ${err.message}`);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = modalType === 'task' ? '/calendar/tasks' : '/calendar/events';
      const method = editingItem ? 'PUT' : 'POST';

      const url = editingItem ? `${endpoint}/${editingItem._id}` : endpoint;

      let payload;
      if (modalType === 'task') {
        payload = {
          title: formData.title,
          description: formData.description,
          due_date: `${formData.date}T${formData.time || '09:00'}:00`,
          due_time: formData.time,
          priority: formData.priority,
          category: formData.category,
          assigned_to: formData.assignedTo || null
        };
      } else {
        payload = {
          title: formData.title,
          description: formData.description,
          start_date: `${formData.date}T${formData.time || '09:00'}:00`,
          event_type: formData.eventType,
          location: formData.location,
          is_all_day: formData.isAllDay,
          color: formData.color
        };
      }

      console.log('Submitting payload:', payload);

      await apiCall(url, {
        method,
        body: JSON.stringify(payload)
      });

      // Refresh data
      await Promise.all([
        fetchCalendarData(),
        fetchInitialData(),
        modalType === 'task' ? fetchTasks() : fetchEvents()
      ]);

      closeModal();
    } catch (err) {
      setError(`Failed to save ${modalType}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Approval workflow for events: an admin's own events are approved on creation (no further approval,
  // they are the head) but still need an explicit publish; teachers' events go HOD -> Admin first.
  const runEventWorkflow = async (id, action) => {
    try {
      await apiCall(`/calendar/event-workflow/${id}/${action}`, { method: 'POST' });
      await Promise.all([fetchCalendarData(), fetchEvents()]);
    } catch (err) {
      setError(err.message || 'That did not work. Please try again.');
    }
  };

  const eventStatusBadge = (event) => {
    const map = {
      draft: ['bg-yellow-100 text-yellow-800', 'Draft (teacher has not submitted)'],
      pending_approval: ['bg-amber-100 text-amber-800', 'Waiting for HOD'],
      pending_admin: ['bg-blue-100 text-blue-800', 'Waiting for admin approval (HODDashboard > Events)'],
      approved: ['bg-emerald-100 text-emerald-800', 'Approved · not published'],
      published: ['bg-green-100 text-green-800', 'Published'],
    };
    const [cls, text] = map[event.status] || ['bg-gray-100 text-gray-700', event.status || 'Published'];
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>{text}</span>;
  };

  // Handle delete
  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      const endpoint = type === 'task' ? '/calendar/tasks' : '/calendar/events';
      await apiCall(`${endpoint}/${id}`, { method: 'DELETE' });

      // Refresh data
      await Promise.all([
        fetchCalendarData(),
        fetchInitialData(),
        type === 'task' ? fetchTasks() : fetchEvents()
      ]);
    } catch (err) {
      setError(`Failed to delete ${type}: ${err.message}`);
    }
  };

  // Modal management
  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);

    if (item) {
      // Edit mode
      setFormData({
        title: item.title || '',
        description: item.description || '',
        date: item.due_date ? item.due_date.split('T')[0] : item.start_date?.split('T')[0] || '',
        time: item.due_time || (item.start_date ? item.start_date.split('T')[1]?.substring(0, 5) : ''),
        priority: item.priority || 'normal',
        category: item.category || 'academic',
        assignedTo: item.assigned_to || '',
        eventType: item.event_type || 'academic',
        location: item.location || '',
        isAllDay: item.is_all_day || false,
        color: item.color || '#3B82F6'
      });
    } else {
      // Create mode
      setFormData({
        title: '',
        description: '',
        date: selectedDate.toISOString().split('T')[0],
        time: '',
        priority: 'normal',
        category: 'academic',
        assignedTo: '',
        eventType: 'academic',
        location: '',
        isAllDay: false,
        color: '#3B82F6'
      });
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setError(null);
  };

  // Calendar navigation
  const previousMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const nextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // Utility functions
  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      important: 'bg-amber-100 text-amber-800 border-amber-200',
      normal: 'bg-green-100 text-green-800 border-green-200',
      low: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[priority] || colors.normal;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.pending;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      academic: '📚',
      administrative: '📋',
      staff: '👥',
      student: '🎓',
      facilities: '🏢',
      events: '🎉',
      meetings: '💼'
    };
    return icons[category] || '📋';
  };

  // Render calendar grid
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Empty cells for days before the first day of the month
    const emptyCells = Array(firstDay).fill(null).map((_, i) => (
      <div key={`empty-${i}`} className="h-24 border border-gray-100"></div>
    ));

    // Generate days
    const days = Array(daysInMonth).fill(null).map((_, i) => {
      const day = i + 1;
      const currentDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = calendarData[currentDateStr] || { events: [], tasks: [] };
      const today = new Date().toISOString().split('T')[0];
      const isToday = currentDateStr === today;

      return (
        <div
          key={`day-${day}`}
          className={`h-24 border border-gray-100 p-2 cursor-pointer hover:bg-blue-50 rounded-lg transition-colors ${isToday ? 'bg-blue-100 border-blue-300' : 'bg-white'
            }`}
          onClick={() => {
            setSelectedDate(new Date(currentDateStr));
            openModal('task');
          }}
        >
          <div className="font-semibold text-gray-800 mb-1">{day}</div>
          <div className="space-y-1">
            {dayData.events.slice(0, 1).map((event, idx) => (
              <div
                key={`event-${idx}`}
                className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 truncate"
                style={{ backgroundColor: event.color + '20', color: event.color }}
              >
                {event.is_acpp_event ? "🧭" : "📅"} {event.title}
              </div>
            ))}
            {dayData.tasks.slice(0, 1).map((task, idx) => (
              <div
                key={`task-${idx}`}
                className={`text-xs px-2 py-1 rounded truncate ${getPriorityColor(task.priority)}`}
              >
                {getCategoryIcon(task.category)} {task.title}
              </div>
            ))}
            {(dayData.events.length + dayData.tasks.length) > 2 && (
              <div className="text-xs text-gray-500">
                +{(dayData.events.length + dayData.tasks.length) - 2} more
              </div>
            )}
          </div>
        </div>
      );
    });

    return (
      <>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={previousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h3 className="text-2xl font-bold text-gray-800 min-w-[200px] text-center">
              {monthName}
            </h3>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
            <div key={`header-${i}`} className="text-center font-semibold text-gray-600 py-3 bg-gray-50 rounded-lg">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {emptyCells}
          {days}
        </div>
      </>
    );
  };

  // Render stats cards
  const renderStats = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Urgent Tasks</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{stats.urgent_tasks || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-red-100">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Important Tasks</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">{stats.important_tasks || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-amber-100">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Completed Today</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.completed_today || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Assigned Tasks</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{stats.assigned_tasks || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-100">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Upcoming Events</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{stats.upcoming_events || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-purple-100">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Overdue Tasks</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{stats.overdue_tasks || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-orange-100">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render task/event modal
  const renderModal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex justify-center items-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-4" style={{ backgroundColor: modalType === 'task' ? '#D67C2C' : '#3B82F6' }}>
            {modalType === 'task' ? <FileText className="h-6 w-6 text-white" /> : <Calendar className="h-6 w-6 text-white" />}
          </div>
          <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
            {editingItem ? 'Edit' : 'Create'} {modalType === 'task' ? 'Task' : 'Event'}
          </h3>
          <p className="text-gray-600 text-center mb-6">
            {editingItem ? 'Update the details below' : `Fill in the details to create a new ${modalType}`}
          </p>

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {modalType === 'task' ? 'Task' : 'Event'} Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder={`Enter ${modalType} title`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Enter description"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>

            {modalType === 'task' ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="urgent">🔴 Urgent</option>
                      <option value="important">🟡 Important</option>
                      <option value="normal">🟢 Normal</option>
                      <option value="low">⚪ Low</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="academic">📚 Academic</option>
                      <option value="administrative">📋 Administrative</option>
                      <option value="staff">👥 Staff Management</option>
                      <option value="student">🎓 Student Affairs</option>
                      <option value="facilities">🏢 Facilities</option>
                      <option value="events">🎉 Events</option>
                      <option value="meetings">💼 Meetings</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assign To</label>
                  <select
                    value={formData.assignedTo}
                    onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select staff member...</option>
                    <option value="">👤 Handle Personally</option>
                    {staffMembers.map(staff => (
                      <option key={staff.id} value={staff.id}>
                        {staff.role === 'teacher' ? '👨‍🏫' : '👤'} {staff.name} - {staff.department || staff.role}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Event Type</label>
                    <select
                      value={formData.eventType}
                      onChange={(e) => setFormData(prev => ({ ...prev, eventType: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="academic">📚 Academic</option>
                      <option value="administrative">📋 Administrative</option>
                      <option value="staff">👥 Staff Meeting</option>
                      <option value="student">🎓 Student Event</option>
                      <option value="facilities">🏢 Facilities</option>
                      <option value="events">🎉 School Event</option>
                      <option value="meetings">💼 Meeting</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full h-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter event location"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isAllDay"
                    checked={formData.isAllDay}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAllDay: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="isAllDay" className="text-sm font-medium text-gray-700">
                    All Day Event
                  </label>
                </div>
              </>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={closeModal}
                disabled={loading}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

          {/* Notification */}
          {error && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 bg-red-500 text-white" style={{ animation: 'slideInRight 0.5s ease-out' }}>
              <AlertCircle className="w-5 h-5" />
              <span className="font-[poppins]">{error}</span>
              <button onClick={() => setError(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header */}
          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#D67C2C' }}>
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                  Calendar Management
                </h1>
                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                  Manage school events and assign tasks to staff members
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 mb-10">
            <button
              onClick={() => openModal('event')}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Calendar className="w-5 h-5" />
              <span>Add Event</span>
            </button>
            <button
              onClick={() => openModal('task')}
              className="flex items-center gap-2 px-6 py-3 bg-[#D67C2C] text-white rounded-xl hover:bg-[#c06a25] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Plus className="w-5 h-5" />
              <span>Add Task</span>
            </button>
          </div>

          {/* Dashboard Cards */}
          {renderStats()}

          {/* View Toggle */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <Filter className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Calendar Views</h2>
                </div>

                <div className="flex bg-gray-100 rounded-xl p-1">
                  <button
                    onClick={() => setView('calendar')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${view === 'calendar'
                      ? 'bg-[#D67C2C] text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                      }`}
                  >
                    Calendar View
                  </button>
                  <button
                    onClick={() => {
                      setView('tasks');
                      fetchTasks();
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${view === 'tasks'
                      ? 'bg-[#D67C2C] text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                      }`}
                  >
                    Tasks View
                  </button>
                  <button
                    onClick={() => {
                      setView('events');
                      fetchEvents();
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${view === 'events'
                      ? 'bg-[#D67C2C] text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                      }`}
                  >
                    Events View
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {view === 'calendar' && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">School Calendar</h2>
                </div>
                {!!acppCalendarRows.length && (
                  <p className="text-xs text-blue-700 mt-2">
                    ACPP Module 10 events are overlaid in this calendar for the mapped academic year.
                  </p>
                )}
              </div>
              <div className="p-6">
                {renderCalendar()}
              </div>
            </div>
          )}

          {view === 'tasks' && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 bg-gray-50">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Tasks Management</h2>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        placeholder="Search by task title..."
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                      />
                    </div>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                    <select
                      value={filters.priority}
                      onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                    >
                      <option value="">All Priority</option>
                      <option value="urgent">Urgent</option>
                      <option value="important">Important</option>
                      <option value="normal">Normal</option>
                      <option value="low">Low</option>
                    </select>
                    <select
                      value={filters.assignedTo}
                      onChange={(e) => setFilters(prev => ({ ...prev, assignedTo: e.target.value }))}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                    >
                      <option value="">All Assignees</option>
                      {distinctAssignees.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={filters.dueDateFrom}
                        onChange={(e) => setFilters(prev => ({ ...prev, dueDateFrom: e.target.value }))}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                        title="Due date from"
                      />
                      <span className="text-gray-400 text-sm">to</span>
                      <input
                        type="date"
                        value={filters.dueDateTo}
                        onChange={(e) => setFilters(prev => ({ ...prev, dueDateTo: e.target.value }))}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                        title="Due date to"
                      />
                    </div>
                    <button
                      onClick={fetchTasks}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Filter className="w-4 h-4" />
                      Filter
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Task</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Due Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Priority</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Assigned To</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedTasks.map(task => (
                      <tr key={task._id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-800">{task.title || 'Untitled Task'}</div>
                          <div className="text-sm text-gray-600">{task.description || 'No description'}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {getCategoryIcon(task.category || 'academic')} {task.category || 'academic'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium">
                            {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No date'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {task.due_time || 'No time'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                            {(task.priority || 'normal').toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium">
                            {task.assigned_to_name || 'Unassigned'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                            {(task.status || 'pending').replace('-', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <ActionsCell>
                            <ActionIconButton
                              icon={Edit}
                              tone="edit"
                              onClick={() => openModal('task', task)}
                              title="Edit"
                            />
                            <ActionIconButton
                              icon={Trash2}
                              tone="delete"
                              onClick={() => handleDelete(task._id, 'task')}
                              title="Delete"
                            />
                          </ActionsCell>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {view === 'events' && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Events Management</h2>
                </div>
              </div>

              <div className="p-6">

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {events.map(event => (
                    <div key={event._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-800">{event.title}</h3>
                        <div className="flex space-x-1">
                          {event.status === 'approved' && ['admin', 'management'].includes(event.created_by_role) && (
                            <button onClick={() => runEventWorkflow(event._id, 'publish')} className="text-emerald-600 hover:text-emerald-800" title="Publish">
                              <Globe className="w-4 h-4" />
                            </button>
                          )}
                          {event.status === 'published' && (
                            <button onClick={() => runEventWorkflow(event._id, 'unpublish')} className="text-gray-600 hover:text-gray-800" title="Unpublish">
                              <EyeOff className="w-4 h-4" />
                            </button>
                          )}
                          {['admin', 'management'].includes(event.created_by_role) && (
                          <button
                            onClick={() => openModal('event', event)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          )}
                          <button
                            onClick={() => handleDelete(event._id, 'event')}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="mb-3">{eventStatusBadge(event)}</div>

                      <p className="text-sm text-gray-600 mb-3">{event.description}</p>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-gray-600">
                          <Calendar className="w-4 h-4 mr-2" />
                          {new Date(event.start_date).toLocaleDateString()}
                        </div>
                        {event.location && (
                          <div className="flex items-center text-gray-600">
                            <span className="w-4 h-4 mr-2">📍</span>
                            {event.location}
                          </div>
                        )}
                        <div className="flex items-center">
                          <span
                            className="w-4 h-4 mr-2 rounded-full border"
                            style={{ backgroundColor: event.color }}
                          ></span>
                          <span className="text-gray-600 capitalize">{event.event_type}</span>
                        </div>
                        {event.created_by_name && (
                          <div className="text-xs text-gray-500">Created by {event.created_by_name}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {renderModal()}
      </div>
    </>
  );
};

export default CalendarManagement;
